import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { RefreshCw, Search, User, MapPin, Calendar, Flag } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

/**
 * AlyNationalViewPage — First screen for the National Transmission role (Aly).
 *
 * It is a STRICTLY READ-ONLY, dispatch-style operational view showing only:
 *   • inspectors who currently have National work assigned
 *   • their live National orders (active_order=yes, not daily-closed, not archived)
 *   • grouped by inspector with appointment / flow / due info
 *
 * Deliberately excluded: assignment controls, edit buttons, route controls, dispatch actions.
 * Aly must only observe "who is holding which National order today".
 */
const AlyNationalViewPage = ({ Layout }) => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({}); // inspector → bool
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      // Backend auto-filters by company=National for National Transmission role.
      const r = await axios.get(`${API}/dispatch/orders`);
      setOrders(r.data || []);
      setLastRefresh(new Date());
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load National orders");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchOrders();
    const i = setInterval(fetchOrders, 30000); // live refresh every 30s
    return () => clearInterval(i);
  }, [fetchOrders]);

  // Live-only filter: active, not closed for the day, not archived.
  // Only National orders that actually have an inspector holding them.
  // Mirrors the Dispatch page filter exactly so counts match the central view.
  const liveNational = useMemo(() => {
    return (orders || []).filter(o =>
      (o.company || "").toLowerCase() === "national"
      && o.active_order !== "no"
      && o.daily_closed !== "yes"
      && o.removed_from_source !== "yes"
      && (o.assigned_inspector || "").trim() !== ""
    );
  }, [orders]);

  // Group by assigned inspector.
  const groups = useMemo(() => {
    const by = new Map();
    const q = search.trim().toLowerCase();
    const matchSearch = (o) => {
      if (!q) return true;
      const hay = [
        o.order_number, o.address, o.city, o.state, o.zip_code,
        o.client, o.cliente, o.ph_name, o.assigned_inspector, o.flow_type,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    };
    liveNational.filter(matchSearch).forEach(o => {
      const insp = o.assigned_inspector;
      if (!by.has(insp)) by.set(insp, []);
      by.get(insp).push(o);
    });
    // Sort inspectors alphabetically; within each, sort by due_date ascending.
    return Array.from(by.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([inspector, list]) => ({
        inspector,
        orders: list.slice().sort((a, b) => (a.due_date || "").localeCompare(b.due_date || "")),
      }));
  }, [liveNational, search]);

  const summary = useMemo(() => {
    const totalOrders = liveNational.length;
    const totalInspectors = groups.length;
    const appointmentSet = liveNational.filter(o => o.appointment_set === "yes").length;
    const inField = liveNational.filter(o => (o.status || "").toLowerCase() === "in field").length;
    const todayStr = new Date().toISOString().slice(0, 10);
    const dueToday = liveNational.filter(o => o.due_date && o.due_date <= todayStr).length;
    return { totalOrders, totalInspectors, appointmentSet, inField, dueToday };
  }, [liveNational, groups]);

  const toggle = (insp) => setExpanded(e => ({ ...e, [insp]: !e[insp] }));

  const content = (
    <div className="space-y-5 p-6 max-w-[1400px] mx-auto" data-testid="aly-national-view">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Flag className="h-5 w-5 text-[#B08A3E]" />
            <h1 className="text-2xl font-bold text-[#1F2937]">National Orders Overview</h1>
            <span className="px-2 py-0.5 bg-[#B08A3E]/10 text-[#B08A3E] rounded-full text-[10px] font-bold uppercase tracking-wider">View only</span>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Live snapshot of every inspector currently holding National work. No actions available — this screen is informational.
          </p>
          {lastRefresh && (
            <p className="text-[10px] text-gray-400 mt-0.5">Refreshed {lastRefresh.toLocaleTimeString()} · auto-refresh every 30s</p>
          )}
        </div>
        <button
          onClick={fetchOrders}
          className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg text-sm text-gray-700"
          data-testid="aly-national-refresh"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <SummaryTile label="Inspectors w/ Work" value={summary.totalInspectors} tone="#B08A3E" />
        <SummaryTile label="Active Orders" value={summary.totalOrders} tone="#2F5D8A" />
        <SummaryTile label="Appointment Set" value={summary.appointmentSet} tone="#6B8F62" />
        <SummaryTile label="Due Today / Overdue" value={summary.dueToday} tone="#C07070" />
        <SummaryTile label="In Field" value={summary.inField} tone="#7B4D8E" />
      </div>

      {/* Search */}
      <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 max-w-md" data-testid="aly-national-search-wrap">
        <Search className="h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search inspector, order, city, address..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="flex-1 text-sm bg-transparent outline-none placeholder-gray-400"
          data-testid="aly-national-search"
        />
        {search && (
          <button onClick={() => setSearch("")} className="text-xs text-gray-400 hover:text-gray-700">clear</button>
        )}
      </div>

      {/* State */}
      {loading && orders.length === 0 && (
        <p className="text-sm text-gray-400 py-10 text-center" data-testid="aly-national-loading">Loading National orders…</p>
      )}
      {err && (
        <p className="text-sm text-red-600 py-4 text-center bg-red-50 border border-red-100 rounded-lg">{err}</p>
      )}
      {!loading && !err && groups.length === 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center" data-testid="aly-national-empty">
          <Flag className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No inspectors currently have National orders assigned.</p>
          <p className="text-[11px] text-gray-400 mt-1">When dispatch assigns a National order to an inspector, they will appear here automatically.</p>
        </div>
      )}

      {/* Groups */}
      <div className="space-y-3">
        {groups.map(g => {
          const isOpen = expanded[g.inspector] !== false; // default open
          return (
            <div key={g.inspector} className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid={`aly-national-group-${g.inspector}`}>
              <button
                type="button"
                onClick={() => toggle(g.inspector)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
                data-testid={`aly-national-group-toggle-${g.inspector}`}
              >
                <div className="flex items-center gap-3">
                  <User className="h-4 w-4 text-[#B08A3E]" />
                  <span className="font-semibold text-[#1F2937] text-sm">{g.inspector}</span>
                  <span className="px-2 py-0.5 rounded-full bg-[#B08A3E]/12 text-[#8A6A2F] text-[10px] font-bold">{g.orders.length} national</span>
                </div>
                <div className="flex items-center gap-2 text-[10px] text-gray-500">
                  {g.orders.filter(o => o.appointment_set === "yes").length > 0 && (
                    <span className="flex items-center gap-1 px-1.5 py-0.5 bg-[#6B8F62]/10 text-[#4F7046] rounded">
                      <Calendar className="h-3 w-3" /> {g.orders.filter(o => o.appointment_set === "yes").length} appt
                    </span>
                  )}
                  <span className="text-gray-400">{isOpen ? "—" : "+"}</span>
                </div>
              </button>

              {isOpen && (
                <div className="border-t border-gray-100 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50/70">
                      <tr className="text-left">
                        <th className="px-3 py-2 font-semibold text-gray-500 uppercase text-[10px] tracking-wider">Due</th>
                        <th className="px-3 py-2 font-semibold text-gray-500 uppercase text-[10px] tracking-wider">Order #</th>
                        <th className="px-3 py-2 font-semibold text-gray-500 uppercase text-[10px] tracking-wider">Type / Flow</th>
                        <th className="px-3 py-2 font-semibold text-gray-500 uppercase text-[10px] tracking-wider">Client</th>
                        <th className="px-3 py-2 font-semibold text-gray-500 uppercase text-[10px] tracking-wider">Address</th>
                        <th className="px-3 py-2 font-semibold text-gray-500 uppercase text-[10px] tracking-wider">City / State</th>
                        <th className="px-3 py-2 font-semibold text-gray-500 uppercase text-[10px] tracking-wider">Appt</th>
                        <th className="px-3 py-2 font-semibold text-gray-500 uppercase text-[10px] tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.orders.map(o => (
                        <tr key={o._id || o.order_number} className="border-t border-gray-100 hover:bg-gray-50/40" data-testid={`aly-national-order-${o.order_number}`}>
                          <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{o.due_date || "—"}</td>
                          <td className="px-3 py-2">
                            <button
                              type="button"
                              onClick={() => o.id && navigate(`/aly-order/${o.id}`)}
                              disabled={!o.id}
                              className="font-mono font-semibold text-[#2F5D8A] hover:underline disabled:text-gray-400"
                              data-testid={`aly-national-open-${o.order_number}`}
                            >
                              {o.order_number}
                            </button>
                          </td>
                          <td className="px-3 py-2">
                            <span className="text-gray-700">{o.order_type || "—"}</span>
                            {o.flow_type && <span className="ml-1 text-[9px] px-1 py-0.5 bg-[#2F5D8A]/8 text-[#2F5D8A] rounded">{o.flow_type}</span>}
                          </td>
                          <td className="px-3 py-2 text-gray-600 truncate max-w-[160px]" title={o.client || o.cliente}>{o.client || o.cliente || "—"}</td>
                          <td className="px-3 py-2 text-gray-600 flex items-center gap-1 min-w-[180px]">
                            <MapPin className="h-3 w-3 text-gray-400 flex-shrink-0" />
                            <span className="truncate">{o.address || "—"}</span>
                          </td>
                          <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                            {o.city || "—"}{o.state ? `, ${o.state}` : ""}
                          </td>
                          <td className="px-3 py-2">
                            {o.appointment_set === "yes" ? (
                              <span className="px-1.5 py-0.5 bg-[#6B8F62]/15 text-[#4F7046] text-[9px] font-bold rounded uppercase">Set</span>
                            ) : (
                              <span className="text-gray-300 text-[9px]">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-gray-600">{o.status || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  return Layout ? <Layout>{content}</Layout> : content;
};

const SummaryTile = ({ label, value, tone }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-3 flex flex-col">
    <span className="text-[9px] uppercase tracking-wider font-bold text-gray-500">{label}</span>
    <span className="text-2xl font-bold mt-0.5" style={{ color: tone }}>{value}</span>
  </div>
);

export default AlyNationalViewPage;
